import { eenmaligeStart } from "../components/eenmaligeStart";
import { useEffect, useMemo, useState } from "react";
import { VestigingFilter } from "../components/VestigingFilter";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import {
  BarChart3,
  CalendarDays,
  CheckCircle2,
  Clock,
  ExternalLink,
  Euro,
  FileText,
  LayoutGrid,
  List,
  MoreVertical,
  PawPrint,
  RotateCcw,
  Settings2,
  Rows3,
  Sliders,
  Store,
  Tag,
  User,
} from "lucide-react";
import { Button, Field, SearchIcon, SelectField, StatusBadge, type StatusTone } from "@fawkes/ui";

import { downloadFactuurPdf } from "../api/factuurExport";
import { formatDate, formatEuro } from "../i18n/format";
import { BetalingOverzicht } from "./BetalingOverzicht";
import { DagafsluitingOverzicht } from "./DagafsluitingOverzicht";
import { OmzetOverzicht } from "./OmzetOverzicht";
import { IndeelbareTabel } from "../components/IndeelbareTabel";
import { PeriodeFilter, periodeBereik } from "../components/PeriodeFilter";

/**
 * Financieel → Overzichten: the invoice overview (maintainer design 2026-08-17). One screen with the
 * finance sub-tabs, a KPI strip, the filter bar and THREE result presentations behind one switch —
 * table, small tiles and large tiles — because that is how the money is read: scanning a list, or
 * comparing a handful of invoices side by side.
 *
 * <p>Data comes from the existing paged {@code GET /api/facturen} feed (the same one FactuurList uses),
 * so this screen adds no api surface. The KPI amounts are summed over the LOADED page and say so — a
 * server-side aggregate over the whole filtered set is the follow-up, and inventing totals that only
 * count 25 rows while the header says "152" would be worse than naming the scope.
 */

interface FactuurListItem {
  id: number;
  nummer: string;
  factuurType: string | null;
  factuurDatum: string | null;
  vervalDatum: string | null;
  status: "BETAALD" | "OPENSTAAND" | "VERVALLEN";
  klantId: number | null;
  klantNaam: string | null;
  totaal: number | null;
  betaald: number | null;
  openstaand: number | null;
  /** Optional enrichments the feed may carry; the cards simply drop what is absent. */
  dierNaam?: string | null;
  onderdeel?: string | null;
  vestigingNaam?: string | null;
  btw?: number | null;
  exclBtw?: number | null;
  eersteHerinnering?: string | null;
  tweedeHerinnering?: string | null;
  incassoDatum?: string | null;
  externeReferentie?: string | null;
  klantBedrijf?: string | null;
  klantAdres?: string | null;
  klantPostcode?: string | null;
  klantWoonplaats?: string | null;
  klantLand?: string | null;
  klantTelefoon?: string | null;
  klantMobiel?: string | null;
  klantEmail?: string | null;
  debiteurnummer?: string | null;
  concept?: boolean;
}

interface FactuurPage {
  items: FactuurListItem[];
  total: number;
  page: number;
  size: number;
  /** The KPI strip over the WHOLE filtered set — see GET /api/facturen/overzicht. */
  totalen?: { omzet: number; betaald: number; openstaand: number; vervallen: number } | null;
}

const PAGE_SIZE = 24;

/**
 * Every column the legacy "Selecteer kolommen" dialog offered, in that order. `vast: true` are the ones
 * the overview always shows (number, date, relation, status and the totals); the rest is switchable, and
 * the choice is remembered per browser.
 */
const KOLOM_DEFS = [
  { key: "dier", vast: false },
  { key: "datum", vast: true },
  { key: "eersteHerinnering", vast: false },
  { key: "onderdeel", vast: false },
  { key: "status", vast: true },
  { key: "relatie", vast: true },
  { key: "btw", vast: false },
  { key: "exclBtw", vast: false },
  { key: "betaald", vast: false },
  { key: "openstaand", vast: true },
  { key: "totaal", vast: true },
  { key: "tweedeHerinnering", vast: false },
  { key: "incassoDatum", vast: false },
  { key: "klantId", vast: false },
  { key: "klantBedrijf", vast: false },
  { key: "klantAdres", vast: false },
  { key: "klantPostcode", vast: false },
  { key: "klantWoonplaats", vast: false },
  { key: "klantLand", vast: false },
  { key: "klantTelefoon", vast: false },
  { key: "klantMobiel", vast: false },
  { key: "klantEmail", vast: false },
  { key: "debiteurnummer", vast: false },
  { key: "externeReferentie", vast: false },
  { key: "vestiging", vast: false },
  { key: "vervaldatum", vast: false },
] as const;
const KOLOMMEN = KOLOM_DEFS.filter((k) => !k.vast).map((k) => k.key);
type Kolom = (typeof KOLOM_DEFS)[number]["key"];
const KOLOM_KEY = "fawkes.financieel.kolommen";
/** What the overview shows until the operator chooses otherwise. */
const STANDAARD: Kolom[] = ["dier", "onderdeel"];

/** Which optional columns the operator last chose (per browser, like the vestiging choice). */
function leesKolommen(): Kolom[] {
  try {
    const raw = globalThis.localStorage?.getItem(KOLOM_KEY);
    if (!raw) return [...STANDAARD];
    const gekozen = JSON.parse(raw) as unknown;
    return Array.isArray(gekozen) ? KOLOMMEN.filter((k) => gekozen.includes(k)) : [...STANDAARD];
  } catch {
    return [...STANDAARD];
  }
}

const STATUS_TONE: Record<FactuurListItem["status"], StatusTone> = {
  BETAALD: "success",
  OPENSTAAND: "warning",
  VERVALLEN: "danger",
};

type Weergave = "tabel" | "klein" | "groot";
type Sortering = "nieuwste" | "oudste" | "bedrag" | "openstaand";

/**
 * The finance sub-tabs (maintainer 2026-08-17: Kasboek and Dagafsluiting are out, Omzet is in). Those two
 * still have their own menu entries; what this screen offers is the invoices, the payments and the turnover
 * read off the same set.
 */
const TABS = ["facturen", "betalingen", "omzet", "dagafsluitingen"] as const;

export function FactuurOverzicht({ onOpenFactuur }: { onOpenFactuur?: (id: number) => void } = {}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [tab, setTab] = useState<(typeof TABS)[number]>("facturen");
  const [weergave, setWeergave] = useState<Weergave>("tabel");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  // De melding "X vervallen facturen" op het dashboard opent dit scherm met dezelfde selectie als de telling.
  const [start] = useState<{ status?: string; periode?: string; vestiging?: string | null }>(() => {
    try {
      const ruw = eenmaligeStart("factuurOverzicht.start");
      return ruw ? JSON.parse(ruw) : {};
    } catch {
      return {};
    }
  });
  const [periode, setPeriode] = useState(start.periode ?? "maand");
  const [eigenPeriode, setEigenPeriode] = useState({ van: "", tot: "" });
  const [status, setStatus] = useState(start.status ?? "");
  const [vestigingen, setVestigingen] = useState<string[]>(start.vestiging ? [start.vestiging] : []);
  const [onderdeel, setOnderdeel] = useState("");
  const [sortering, setSortering] = useState<Sortering>("nieuwste");
  const [bedragVanaf, setBedragVanaf] = useState("");
  const [alleenOpenstaand, setAlleenOpenstaand] = useState(false);
  const [page, setPage] = useState(1);
  const [kolommen, setKolommen] = useState<Kolom[]>(leesKolommen);
  const [veldenOpen, setVeldenOpen] = useState(false);
  const [menuVoor, setMenuVoor] = useState<number | null>(null);
  const [pdfFout, setPdfFout] = useState(false);
  // Via een geauthenticeerde fetch, niet via een link: het token zit alleen in het geheugen, en een
  // kale <a href> gaf daardoor een 401 in een nieuw tabblad.
  const pdf = (id: number, nummer: string | null) => {
    setMenuVoor(null);
    setPdfFout(false);
    downloadFactuurPdf(String(id), nummer || String(id)).catch(() => setPdfFout(true));
  };
  const toonKolom = (k: Kolom) => kolommen.includes(k);
  const zetKolommen = (next: Kolom[]) => {
    setKolommen(next);
    try { globalThis.localStorage?.setItem(KOLOM_KEY, JSON.stringify(next)); } catch { /* niet erg */ }
  };
  const wisselKolom = (k: Kolom) =>
    zetKolommen(kolommen.includes(k) ? kolommen.filter((x) => x !== k) : [...kolommen, k]);

  useEffect(() => {
    const handle = setTimeout(() => { setDebounced(query.trim()); setPage(1); }, 300);
    return () => clearTimeout(handle);
  }, [query]);
  useEffect(() => { setPage(1); }, [periode, status, vestigingen, onderdeel, bedragVanaf, alleenOpenstaand]);

  // "Deze maand" / "Dit kwartaal" / "Dit jaar" / "Alles" → the feed's from/to bounds.
  const bereik = useMemo(() => periodeBereik(periode, new Date(), eigenPeriode), [periode, eigenPeriode]);

  // Klik ernaast (of Escape) klapt de open menu's dicht — een dropdown die alleen sluit door hem weer
  // aan te tikken staat in de weg van het scherm eronder.
  useEffect(() => {
    // De vestigingkiezer sluit zichzelf; hier blijven de velden-lijst en het rijmenu over.
    if (!veldenOpen && menuVoor == null) return;
    const sluit = () => { setVeldenOpen(false); setMenuVoor(null); };
    const buiten = (e: MouseEvent) => {
      const doel = e.target as HTMLElement | null;
      if (doel?.closest(".fawkes-fin__multi, .fawkes-fin__velden, .fawkes-fin__rowmenu")) return;
      sluit();
    };
    const toets = (e: KeyboardEvent) => { if (e.key === "Escape") sluit(); };
    document.addEventListener("mousedown", buiten);
    document.addEventListener("keydown", toets);
    return () => {
      document.removeEventListener("mousedown", buiten);
      document.removeEventListener("keydown", toets);
    };
  }, [veldenOpen, menuVoor]);

  // Every control is a server parameter: the page, the count and the KPI amounts then describe one and
  // the same set. Filtering a loaded page in the browser could only ever narrow those 24 rows.
  const url = useMemo(() => {
    const params = new URLSearchParams();
    if (debounced) params.set("q", debounced);
    if (bereik.from) params.set("from", bereik.from);
    if (bereik.to) params.set("to", bereik.to);
    vestigingen.forEach((v) => params.append("vestiging", v));
    if (onderdeel) params.set("onderdeel", onderdeel);
    if (status) params.set("status", status);
    const min = bedragVanaf.replace(",", ".").trim();
    if (min && Number.isFinite(Number(min))) params.set("bedragVanaf", min);
    if (alleenOpenstaand) params.set("alleenOpenstaand", "true");
    params.set("sort", sortering);
    params.set("page", String(page));
    params.set("size", String(PAGE_SIZE));
    return `facturen/overzicht?${params.toString()}`;
  }, [debounced, bereik, vestigingen, onderdeel, status, bedragVanaf, alleenOpenstaand, sortering, page]);

  // Every branch the user may see — the filter lists them all, so a branch with no invoices this month
  // can still be picked (deriving the options from the page would hide exactly those).
  const { data: vestigingFeed } = useCustom<{ id: number; naam: string }[]>({
    url: "vestigingen", method: "get",
  });
  const alleVestigingen = useMemo(() => {
    const rijen = vestigingFeed?.data;
    const namen = Array.isArray(rijen)
      ? rijen.map((v) => (v?.naam ?? "").trim()).filter(Boolean)
      : [];
    return [...new Set(namen)].sort((a, b) => a.localeCompare(b));
  }, [vestigingFeed]);

  // Typen op een deel van de naam: negentien vestigingen doorscrollen om er twee te vinden is werk dat
  // een zoekregel overneemt.

  const { data, isLoading, isError, refetch } = useCustom<FactuurPage>({ url, method: "get" });
  const alle = data?.data?.items ?? [];
  const totaalAantal = data?.data?.total ?? 0;

  const rijen = alle;

  const gesorteerd = rijen;

  // Filter options come from what is actually on screen: a value you cannot pick is a dead control.
  const onderdelen = useMemo(
    () => [...new Set([...alle.map(onderdeelLabel).filter((v) => v !== "—"), onderdeel].filter(Boolean))].sort(),
    [alle, onderdeel],
  );

  const heeftDier = useMemo(() => alle.some((r) => (r.dierNaam ?? "").trim() !== ""), [alle]);
  const heeftOnderdeel = onderdelen.length > 0;
  const heeftVestiging = alleVestigingen.length > 0 || alle.some((r) => (r.vestigingNaam ?? "").trim() !== "");

  // The four amounts come from the server aggregate over the filtered set (never summed per page).
  const totalen = data?.data?.totalen ?? null;
  const kpi = {
    omzet: totalen?.omzet ?? 0,
    openstaand: totalen?.openstaand ?? 0,
    betaald: totalen?.betaald ?? 0,
    vervallen: totalen?.vervallen ?? 0,
  };

  const wissen = () => {
    setQuery(""); setPeriode("maand"); setStatus(""); setVestigingen([]); setOnderdeel("");
    setBedragVanaf(""); setAlleenOpenstaand(false); setSortering("nieuwste");
  };

  return (
    <div className="fawkes-fin">
      <nav className="fawkes-fin__tabs" role="tablist" aria-label={t("financieel.title")}>
        {TABS.map((k) => (
          <button
            key={k} type="button" role="tab" aria-selected={tab === k}
            className={`fawkes-fin__tab${tab === k ? " is-active" : ""}`}
            onClick={() => setTab(k)}
          >
            {t(`financieel.tab.${k}`)}
          </button>
        ))}
      </nav>

      {tab === "betalingen" ? (
        <BetalingOverzicht onOpenFactuur={onOpenFactuur} />
      ) : tab === "dagafsluitingen" ? (
        <DagafsluitingOverzicht />
      ) : tab === "omzet" ? (
        <OmzetOverzicht onOpenFactuur={onOpenFactuur} />
      ) : tab !== "facturen" ? (
        <div className="fawkes-fin__soon">{t("financieel.tab.soon")}</div>
      ) : (
        <>
          <header className="fawkes-fin__head">
            <span className="fawkes-fin__head-icon" aria-hidden="true"><FileText size={26} /></span>
            <div className="fawkes-fin__head-text">
              <h2>{t("financieel.overzicht.title")}</h2>
              <p>{t("financieel.overzicht.subtitle")}</p>
            </div>
            <div className="fawkes-fin__kpis">
              <Kpi icon={<BarChart3 size={18} />} value={formatEuro(kpi.omzet, locale)} label={t("financieel.kpi.omzet")} />
              <Kpi icon={<Euro size={18} />} value={formatEuro(kpi.openstaand, locale)} label={t("financieel.kpi.openstaand")} />
              <Kpi icon={<CheckCircle2 size={18} />} value={formatEuro(kpi.betaald, locale)} label={t("financieel.kpi.betaald")} />
              <Kpi icon={<Clock size={18} />} value={formatEuro(kpi.vervallen, locale)} label={t("financieel.kpi.vervallen")} />
              <Kpi icon={<FileText size={18} />} value={String(totaalAantal)} label={t("financieel.kpi.totaal")} accent />
            </div>
          </header>

          <section className="fawkes-fin__filters">
            <div className="fawkes-fin__searchcell">
              <span className="fui-field__label" aria-hidden="true">&nbsp;</span>
              <label className="fawkes-fin__search">
              <span className="fawkes-fin__search-icon" aria-hidden="true">
                <SearchIcon width={18} height={18} />
              </span>
              <input
                value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder={t("financieel.filter.zoek")} aria-label={t("financieel.filter.zoek")}
              />
              </label>
            </div>
              <PeriodeFilter waarde={periode} onWaarde={setPeriode} eigen={eigenPeriode} onEigen={setEigenPeriode} />
            {heeftVestiging && (
              <VestigingFilter
                label={t("financieel.filter.vestiging")}
                opties={alleVestigingen}
                gekozen={vestigingen}
                onChange={setVestigingen}
              />
            )}
            {heeftOnderdeel && <SelectField
              label={t("financieel.filter.onderdeel")} value={onderdeel}
              onChange={(e) => setOnderdeel(e.target.value)}
              options={[{ value: "", label: t("financieel.filter.alle") },
                ...onderdelen.map((v) => ({ value: v, label: v }))]}
            />}
            <SelectField
              label={t("financieel.filter.status")} value={status}
              onChange={(e) => setStatus(e.target.value)}
              options={[
                { value: "", label: t("financieel.filter.alle") },
                { value: "OPENSTAAND", label: t("factuurListView.status.OPENSTAAND") },
                { value: "BETAALD", label: t("factuurListView.status.BETAALD") },
                { value: "VERVALLEN", label: t("factuurListView.status.VERVALLEN") },
              ]}
            />
            <Field
              label={t("financieel.filter.bedragVanaf")} type="number" min={0} step={5}
              value={bedragVanaf} placeholder="0"
              onChange={(e) => setBedragVanaf(e.target.value)}
            />
            <div className="fawkes-fin__filters-actions">
              <span className="fui-field__label" aria-hidden="true">&nbsp;</span>
              <Button variant="secondary" onClick={wissen}>
                <RotateCcw size={14} /> {t("financieel.filter.wissen")}
              </Button>
            </div>
            {/* A real switch with its state spelled out beside it, like the design — a bare checkbox
                left you guessing whether "alleen openstaand" was on. */}
            <div className="fawkes-fin__toggle">
              <button
                type="button" role="switch" aria-checked={alleenOpenstaand}
                className={`fawkes-fin__switch${alleenOpenstaand ? " is-on" : ""}`}
                onClick={() => setAlleenOpenstaand((v) => !v)}
              >
                <span className="fawkes-fin__switch-knob" />
              </button>
              <span className="fawkes-fin__toggle-label">{t("financieel.filter.alleenOpenstaand")}</span>
              <span className="fawkes-fin__toggle-state">
                {t(alleenOpenstaand ? "financieel.filter.actief" : "financieel.filter.inactief")}
              </span>
            </div>
          </section>

          <div className="fawkes-fin__resultbar">
            <span className="fawkes-fin__count">
              <Sliders size={14} aria-hidden="true" />
              {t("financieel.resultaten", { args: [String(totaalAantal)] })}
            </span>
            <div className="fawkes-fin__resultbar-right">
                <div className="fawkes-fin__velden">
                <Button variant="secondary" onClick={() => setVeldenOpen((v) => !v)}>
                  <Settings2 size={14} /> {t("financieel.velden")}
                </Button>
                {veldenOpen && (
                  <div className="fawkes-fin__velden-menu" role="group" aria-label={t("financieel.velden")}>
                    <button
                      type="button" className="fawkes-fin__multi-all"
                      onClick={() => zetKolommen(kolommen.length === KOLOMMEN.length ? [] : [...KOLOMMEN])}
                    >
                      {kolommen.length === KOLOMMEN.length
                        ? t("financieel.filter.allesUit")
                        : t("financieel.filter.allesAan")}
                    </button>
                    <div className="fawkes-fin__velden-list">
                      {KOLOMMEN.map((k) => (
                        <label key={k}>
                          <input type="checkbox" checked={toonKolom(k)} onChange={() => wisselKolom(k)} />
                          <span>{t(`financieel.kolom.${k}`)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            <div className="fawkes-fin__views" role="group" aria-label={t("financieel.weergave")}>
                <ViewBtn active={weergave === "tabel"} onClick={() => setWeergave("tabel")}
                  title={t("financieel.weergave.tabel")}><List size={16} /></ViewBtn>
                <ViewBtn active={weergave === "klein"} onClick={() => setWeergave("klein")}
                  title={t("financieel.weergave.klein")}><LayoutGrid size={16} /></ViewBtn>
                <ViewBtn active={weergave === "groot"} onClick={() => setWeergave("groot")}
                  title={t("financieel.weergave.groot")}><Rows3 size={16} /></ViewBtn>
              </div>
              <label className="fawkes-fin__sort">
                <span>{t("financieel.sorteren")}</span>
                <select value={sortering} onChange={(e) => setSortering(e.target.value as Sortering)}>
                  <option value="nieuwste">{t("financieel.sorteren.nieuwste")}</option>
                  <option value="oudste">{t("financieel.sorteren.oudste")}</option>
                  <option value="bedrag">{t("financieel.sorteren.bedrag")}</option>
                  <option value="openstaand">{t("financieel.sorteren.openstaand")}</option>
                </select>
              </label>
            </div>
          </div>

          {pdfFout && (
            <p className="fawkes-fin__state fawkes-fin__state--error" role="alert">
              {t("factuurDetailView.notice.exportError")}
            </p>
          )}
          {isLoading && <p className="fawkes-fin__state">{t("common.loading")}</p>}
          {isError && (
            <p className="fawkes-fin__state fawkes-fin__state--error">
              {t("common.loadError")}{" "}
              <Button variant="secondary" onClick={() => refetch()}>{t("common.retry")}</Button>
            </p>
          )}
          {!isLoading && !isError && rijen.length === 0 && (
            <p className="fawkes-fin__state">{t("financieel.leeg")}</p>
          )}

          {!isLoading && !isError && rijen.length > 0 && weergave === "tabel" && (
            <div className="fawkes-fin__tablewrap">
              <IndeelbareTabel
                tabelKey="factuuroverzicht"
                columns={[
              {
                key: "nummer", header: t("financieel.kolom.nummer"),
                cell: (r) => (
                  <button type="button" className="fawkes-fin__link" onClick={() => onOpenFactuur?.(r.id)}>
                    {r.nummer || t("financieel.concept")}
                  </button>
                ),
              },
              { key: "datum", header: t("financieel.kolom.datum"),
                cell: (r) => <span className="tabular">{formatDate(r.factuurDatum, locale)}</span> },
              { key: "relatie", header: t("financieel.kolom.relatie"), cell: (r) => r.klantNaam ?? "—" },
              ...(heeftDier && toonKolom("dier") ? [{
                key: "dier", header: t("financieel.kolom.dier"),
                cell: (r: typeof gesorteerd[number]) => r.dierNaam ?? "—",
              }] : []),
              ...(heeftOnderdeel && toonKolom("onderdeel") ? [{
                key: "onderdeel", header: t("financieel.kolom.onderdeel"),
                cell: (r: typeof gesorteerd[number]) => onderdeelLabel(r),
              }] : []),
              ...(heeftVestiging && toonKolom("vestiging") ? [{
                key: "vestiging", header: t("financieel.kolom.vestiging"),
                cell: (r: typeof gesorteerd[number]) => r.vestigingNaam ?? "—",
              }] : []),
              ...(toonKolom("vervaldatum") ? [{
                key: "vervaldatum", header: t("financieel.kolom.vervaldatum"),
                // Een vervallen factuur kleurt zijn vervaldatum rood; dat is de hele reden dat die
                // kolom bestaat.
                cell: (r: typeof gesorteerd[number]) => (
                  <span className={r.status === "VERVALLEN" ? "tabular is-danger" : "tabular"}>
                    {formatDate(r.vervalDatum, locale)}
                  </span>
                ),
              }] : []),
              ...(toonKolom("eersteHerinnering") ? [{
                key: "eersteHerinnering", header: t("financieel.kolom.eersteHerinnering"),
                cell: (r: typeof gesorteerd[number]) => <span className="tabular">{formatDate(r.eersteHerinnering ?? null, locale)}</span>,
              }] : []),
              ...(toonKolom("tweedeHerinnering") ? [{
                key: "tweedeHerinnering", header: t("financieel.kolom.tweedeHerinnering"),
                cell: (r: typeof gesorteerd[number]) => <span className="tabular">{formatDate(r.tweedeHerinnering ?? null, locale)}</span>,
              }] : []),
              ...(toonKolom("incassoDatum") ? [{
                key: "incassoDatum", header: t("financieel.kolom.incassoDatum"),
                cell: (r: typeof gesorteerd[number]) => <span className="tabular">{formatDate(r.incassoDatum ?? null, locale)}</span>,
              }] : []),
              ...(toonKolom("klantId") ? [{
                key: "klantId", header: t("financieel.kolom.klantId"),
                cell: (r: typeof gesorteerd[number]) => <span className="is-num tabular">{r.klantId ?? "—"}</span>,
              }] : []),
              ...(toonKolom("klantBedrijf") ? [{
                key: "klantBedrijf", header: t("financieel.kolom.klantBedrijf"),
                cell: (r: typeof gesorteerd[number]) => r.klantBedrijf || "—",
              }] : []),
              ...(toonKolom("klantAdres") ? [{
                key: "klantAdres", header: t("financieel.kolom.klantAdres"),
                cell: (r: typeof gesorteerd[number]) => r.klantAdres || "—",
              }] : []),
              ...(toonKolom("klantPostcode") ? [{
                key: "klantPostcode", header: t("financieel.kolom.klantPostcode"),
                cell: (r: typeof gesorteerd[number]) => r.klantPostcode || "—",
              }] : []),
              ...(toonKolom("klantWoonplaats") ? [{
                key: "klantWoonplaats", header: t("financieel.kolom.klantWoonplaats"),
                cell: (r: typeof gesorteerd[number]) => r.klantWoonplaats || "—",
              }] : []),
              ...(toonKolom("klantLand") ? [{
                key: "klantLand", header: t("financieel.kolom.klantLand"),
                cell: (r: typeof gesorteerd[number]) => r.klantLand || "—",
              }] : []),
              ...(toonKolom("klantTelefoon") ? [{
                key: "klantTelefoon", header: t("financieel.kolom.klantTelefoon"),
                cell: (r: typeof gesorteerd[number]) => r.klantTelefoon || "—",
              }] : []),
              ...(toonKolom("klantMobiel") ? [{
                key: "klantMobiel", header: t("financieel.kolom.klantMobiel"),
                cell: (r: typeof gesorteerd[number]) => r.klantMobiel || "—",
              }] : []),
              ...(toonKolom("klantEmail") ? [{
                key: "klantEmail", header: t("financieel.kolom.klantEmail"),
                cell: (r: typeof gesorteerd[number]) => r.klantEmail || "—",
              }] : []),
              ...(toonKolom("debiteurnummer") ? [{
                key: "debiteurnummer", header: t("financieel.kolom.debiteurnummer"),
                cell: (r: typeof gesorteerd[number]) => r.debiteurnummer || "—",
              }] : []),
              ...(toonKolom("externeReferentie") ? [{
                key: "externeReferentie", header: t("financieel.kolom.externeReferentie"),
                cell: (r: typeof gesorteerd[number]) => r.externeReferentie || "—",
              }] : []),
              {
                key: "status", header: t("financieel.kolom.status"),
                cell: (r) => (
                  <StatusBadge tone={STATUS_TONE[r.status]}>
                    {t(`factuurListView.status.${r.status}`)}
                  </StatusBadge>
                ),
              },
              ...(toonKolom("exclBtw") ? [{
                key: "exclBtw", header: t("financieel.kolom.exclBtw"),
                cell: (r: typeof gesorteerd[number]) => <span className="is-num tabular">{formatEuro(r.exclBtw ?? null, locale)}</span>,
              }] : []),
              ...(toonKolom("btw") ? [{
                key: "btw", header: t("financieel.kolom.btw"),
                cell: (r: typeof gesorteerd[number]) => <span className="is-num tabular">{formatEuro(r.btw ?? null, locale)}</span>,
              }] : []),
              { key: "totaal", header: t("financieel.kolom.totaal"),
                cell: (r) => <span className="is-num tabular">{formatEuro(r.totaal, locale)}</span> },
              ...(toonKolom("betaald") ? [{
                key: "betaald", header: t("financieel.kolom.betaald"),
                cell: (r: typeof gesorteerd[number]) => <span className="is-num tabular">{formatEuro(r.betaald, locale)}</span>,
              }] : []),
              { key: "openstaand", header: t("financieel.kolom.openstaand"),
                cell: (r) => <span className="is-num tabular">{formatEuro(r.openstaand, locale)}</span> },
              {
                key: "acties", header: "", vast: true,
                cell: (r) => (
                  <div className="fawkes-fin__rowmenu">
                    <button
                      type="button" className="fawkes-fin__kebab" title={t("common.more")}
                      aria-haspopup="true" aria-expanded={menuVoor === r.id}
                      onClick={(e) => { e.stopPropagation(); setMenuVoor(menuVoor === r.id ? null : r.id); }}
                    >
                      <MoreVertical size={16} />
                    </button>
                    {menuVoor === r.id && (
                      <div className="fawkes-fin__rowmenu-items" role="menu">
                        <button type="button" role="menuitem"
                          onClick={() => { setMenuVoor(null); onOpenFactuur?.(r.id); }}>
                          {t("financieel.actie.openen")}
                        </button>
                        <button type="button" role="menuitem" onClick={() => pdf(r.id, r.nummer)}>
                          {t("financieel.actie.pdf")}
                        </button>
                      </div>
                    )}
                  </div>
                ),
              },
                ]}
                rows={gesorteerd}
                getRowKey={(r) => String(r.id)}
                empty=""
              />
            </div>
          )}

          {!isLoading && !isError && rijen.length > 0 && weergave !== "tabel" && (
            <div className={`fawkes-fin__grid fawkes-fin__grid--${weergave}`}>
              {gesorteerd.map((r) => (
                <article key={r.id} className="fawkes-fin__card">
                  <header className="fawkes-fin__card-head">
                    <span className="fawkes-fin__card-nr">{r.nummer || t("financieel.concept")}</span>
                    <StatusBadge tone={STATUS_TONE[r.status]}>
                      {t(`factuurListView.status.${r.status}`)}
                    </StatusBadge>
                    <button type="button" className="fawkes-fin__kebab" title={t("common.more")}
                      onClick={() => onOpenFactuur?.(r.id)}>
                      <MoreVertical size={16} />
                    </button>
                  </header>

                  <div className="fawkes-fin__card-body">
                    <dl>
                      <Rij icon={<User size={13} />} label={t("financieel.kolom.relatie")} value={r.klantNaam} />
                      {heeftDier && (
                        <Rij icon={<PawPrint size={13} />} label={t("financieel.kolom.dier")} value={r.dierNaam} />
                      )}
                      <Rij icon={<CalendarDays size={13} />} label={t("financieel.kolom.datum")}
                        value={formatDate(r.factuurDatum, locale)} />
                    </dl>
                    {weergave === "groot" ? (
                      <dl>
                        {heeftVestiging && (
                          <Rij icon={<Store size={13} />} label={t("financieel.kolom.vestiging")} value={r.vestigingNaam} />
                        )}
                        <Rij icon={<Tag size={13} />} label={t("financieel.kolom.onderdeel")}
                          value={onderdeelLabel(r)} />
                        <Rij icon={<Clock size={13} />} label={t("financieel.kolom.vervaldatum")}
                          value={formatDate(r.vervalDatum, locale)} danger={r.status === "VERVALLEN"} />
                      </dl>
                    ) : (
                      <dl>
                        <Rij icon={<Tag size={13} />} label={t("financieel.kolom.onderdeel")}
                          value={onderdeelLabel(r)} />
                      </dl>
                    )}
                  </div>

                  <footer className="fawkes-fin__card-foot">
                    <Bedrag label={t("financieel.kolom.totaal")} value={formatEuro(r.totaal, locale)} />
                    {weergave === "groot" && (
                      <Bedrag label={t("financieel.kolom.betaald")} value={formatEuro(r.betaald, locale)} />
                    )}
                    <Bedrag
                      label={t("financieel.kolom.openstaand")} value={formatEuro(r.openstaand, locale)}
                      danger={(r.openstaand ?? 0) > 0}
                    />
                  </footer>

                  {weergave === "groot" && (
                    <div className="fawkes-fin__card-actions">
                      <Button variant="secondary" onClick={() => onOpenFactuur?.(r.id)}>
                        {t("financieel.actie.openen")} <ExternalLink size={13} />
                      </Button>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}

          {totaalAantal > PAGE_SIZE && (
            <div className="fawkes-fin__pager">
              <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                {t("common.previous")}
              </Button>
              <span className="fawkes-fin__pageinfo">
                {t("financieel.pagina", { args: [String(page), String(Math.ceil(totaalAantal / PAGE_SIZE))] })}
              </span>
              <Button
                variant="secondary" disabled={page >= Math.ceil(totaalAantal / PAGE_SIZE)}
                onClick={() => setPage((p) => p + 1)}
              >
                {t("common.next")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ icon, value, label, accent }: {
  icon: React.ReactNode; value: string; label: string; accent?: boolean;
}) {
  return (
    <div className={`fawkes-fin__kpi${accent ? " is-accent" : ""}`}>
      <span className="fawkes-fin__kpi-icon" aria-hidden="true">{icon}</span>
      <span className="fawkes-fin__kpi-text">
        <strong>{value}</strong>
        <small>{label}</small>
      </span>
    </div>
  );
}

function ViewBtn({ active, onClick, title, children }: {
  active: boolean; onClick: () => void; title: string; children: React.ReactNode;
}) {
  return (
    <button
      type="button" className={`fawkes-fin__view${active ? " is-active" : ""}`}
      aria-pressed={active} title={title} aria-label={title} onClick={onClick}
    >
      {children}
    </button>
  );
}

function Rij({ icon, label, value, danger }: {
  icon: React.ReactNode; label: string; value: string | null | undefined; danger?: boolean;
}) {
  return (
    <div className="fawkes-fin__rij">
      <dt><span aria-hidden="true">{icon}</span> {label}</dt>
      <dd className={danger ? "is-danger" : undefined}>{value?.trim() || "—"}</dd>
    </div>
  );
}

function Bedrag({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="fawkes-fin__bedrag">
      <small>{label}</small>
      <strong className={danger ? "is-danger" : undefined}>{value}</strong>
    </div>
  );
}

/**
 * The "Onderdeel" cell: the feed may carry a real module name; the legacy one-letter factuur_type is
 * not a label a user can read, so it never reaches the screen.
 */
function onderdeelLabel(r: FactuurListItem): string {
  const v = r.onderdeel?.trim();
  if (v) return v;
  const type = r.factuurType?.trim();
  return type && type.length > 1 ? type : "—";
}
